import type { ReactElement, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { TriangleAlert, KeyRound, RefreshCw, UsersRound } from "lucide-react";
import { Card, CardBody, Link, StatTile, StatusPill } from "@playbookai/ui";
import { useAuthStore } from "../../auth/auth-store.js";
import {
  getTenantConfiguration,
  tenantConfigurationQueryKey,
} from "../../api/tenant-config-api.js";
import {
  directoryStatusQueryKey,
  getDirectoryStatus,
} from "../../api/directory-status-api.js";
import {
  listProjectionFailures,
  projectionFailuresQueryKey,
} from "../../api/admin-projection-api.js";
import { getTeamAdminSummary } from "../../api/teams-api.js";
import { useDateTimeFormatter } from "../../settings/format-date-time.js";
import { AdminTimeZoneLabel } from "./AdminTimeZoneLabel.js";
import { ADMIN_MEMBERS_ROUTE } from "../../routes.js";

export function IdentityOverviewTab(): ReactElement {
  const formatter = useDateTimeFormatter();
  const { dateTimeOr } = formatter;
  const tenantId = useAuthStore((state) => state.claims?.tenantId);
  const configuration = useQuery({
    queryKey: tenantConfigurationQueryKey(tenantId),
    queryFn: getTenantConfiguration,
  });
  const directory = useQuery({
    queryKey: directoryStatusQueryKey(tenantId),
    queryFn: getDirectoryStatus,
  });
  const teams = useQuery({
    queryKey: ["identity-overview", "teams", tenantId ?? "none"],
    queryFn: getTeamAdminSummary,
  });
  const failures = useQuery({
    queryKey: [...projectionFailuresQueryKey(tenantId), "overview"],
    queryFn: () => listProjectionFailures(null),
  });
  const config = configuration.data;
  const directoryData = directory.data;
  const entraEvidence = directoryData?.entra;
  const lastDirectoryActivity = directoryData?.sources
    .map((source) => source.lastActivityAt)
    .filter((value): value is string => Boolean(value))
    .sort()
    .at(-1);

  /* Each card states its own status (PB-5404).
     All four used to take their pill wording from `tone` alone, through one
     four-word vocabulary — Healthy / In progress / Attention / Not configured.
     That vocabulary cannot say "configured but never run", which is how a
     directory source with no run behind it reported HEALTHY next to its own
     "No activity yet", and how a count of teams read as a health verdict.
     `tone` now carries only the colour; the wording is the card's own. */
  const entraSource = directoryData?.sources.find(
    (source) => source.source === "microsoft_entra",
  );
  /* Read from live sign-in evidence, NOT from `identityProvider`, which only the
     deployment bootstrap writes. The members roster derives its per-member
     "Microsoft Entra" tag from these same rows, so the two can no longer
     disagree — on dev this card claimed "Not connected" beside 230+ members
     tagged Microsoft Entra. */
  const entraStatus: CardStatus = directory.isError
    ? { status: "Attention", tone: "critical" }
    : !directoryData
      ? { status: "Checking", tone: "neutral" }
      : !entraEvidence
        ? { status: "Not available", tone: "neutral" }
        : entraEvidence.linkedMemberCount > 0
          ? { status: "In use", tone: "healthy" }
          : entraSource?.configured
            ? { status: "No sign-ins yet", tone: "neutral" }
            : { status: "Not set up", tone: "neutral" };
  /* Health requires EVIDENCE — a run that finished or a provider that actually
     called us — never merely an issued credential. An active SCIM client with a
     null `lastUsedAt` is what used to make this read Healthy. */
  const directorySyncStatus: CardStatus = directory.isError
    ? { status: "Attention", tone: "critical" }
    : !directoryData
      ? { status: "Checking", tone: "neutral" }
      : directoryData.sources.some(
            (source) => source.lastRun?.status === "failed",
          )
        ? { status: "Attention", tone: "critical" }
        : lastDirectoryActivity
          ? { status: "Healthy", tone: "healthy" }
          : directoryData.sources.some((source) => source.configured)
            ? { status: "Never run", tone: "neutral" }
            : { status: "Not set up", tone: "neutral" };
  /* A count of teams is not a health verdict, so it no longer claims one. */
  const teamsStatus: CardStatus = teams.isError
    ? { status: "Attention", tone: "critical" }
    : !teams.data
      ? { status: "Checking", tone: "neutral" }
      : teams.data.activeTeams > 0
        ? { status: "Active", tone: "healthy" }
        : { status: "None yet", tone: "neutral" };
  /* An empty queue is "up to date", not "healthy": nothing has been proven well,
     there is simply nothing waiting.
     PB-5435: a change held until a member's first sign-in is not queued work —
     the worker never picks it up — so counting it kept this card "In progress"
     for as long as anyone invited had not signed in. It is listed, not counted.
     Work the worker has stopped moving is a fault, not progress. */
  const waitingCount = config?.sync.waitingIdentityCount ?? 0;
  const stalledCount = config?.sync.stalledCount ?? 0;
  const accessSyncStatus: CardStatus = configuration.isError
    ? { status: "Attention", tone: "critical" }
    : !config
      ? { status: "Checking", tone: "neutral" }
      : config.sync.deadLetterCount > 0 || stalledCount > 0
        ? { status: "Attention", tone: "critical" }
        : config.sync.pendingCount > 0
          ? { status: "In progress", tone: "watch" }
          : { status: "Up to date", tone: "healthy" };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-pb-text">
          Identity overview
        </h2>
        <p className="mt-1 text-sm text-pb-text-secondary">
          Sign-in, directory sync, provisioning and shared Teams in one
          operational view.
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-2 @min-[58.5rem]/settings-page:grid-cols-4">
        <SummaryCard
          icon={KeyRound}
          label="Microsoft Entra sign-in"
          value={
            directory.isError
              ? "Unavailable"
              : !directoryData
                ? "Loading…"
                : entraEvidence
                  ? `${entraEvidence.linkedMemberCount} ${entraEvidence.linkedMemberCount === 1 ? "member" : "members"}`
                  : "Not available"
          }
          detail={
            directory.isError ? (
              "Could not load sign-in status."
            ) : !directoryData ? (
              "Checking sign-in"
            ) : !entraEvidence ? (
              "Live sign-in evidence is not available yet."
            ) : entraEvidence.lastSignInAt ? (
              <>
                Last sign-in{" "}
                <AdminTimeZoneLabel
                  label={dateTimeOr(entraEvidence.lastSignInAt, "—")}
                  formatter={formatter}
                  variant="inline"
                />
              </>
            ) : (
              "Nobody has signed in with Entra yet."
            )
          }
          status={entraStatus.status}
          tone={entraStatus.tone}
        />
        <SummaryCard
          icon={RefreshCw}
          label="Directory sync"
          value={
            directory.isError
              ? "Unavailable"
              : directoryData
                ? `${directoryData.people.activeCount} people`
                : "Loading…"
          }
          detail={
            directory.isError ? (
              "Could not load directory health."
            ) : directoryData ? (
              lastDirectoryActivity ? (
                <>
                  {`${directoryData.groups.activeCount} directory groups · `}
                  <AdminTimeZoneLabel
                    label={dateTimeOr(lastDirectoryActivity, "—")}
                    formatter={formatter}
                    variant="inline"
                  />
                </>
              ) : (
                `${directoryData.groups.activeCount} directory groups · No activity yet`
              )
            ) : (
              "Checking directory health"
            )
          }
          status={directorySyncStatus.status}
          tone={directorySyncStatus.tone}
        />
        <SummaryCard
          icon={UsersRound}
          label="Shared teams"
          value={
            teams.isError
              ? "Unavailable"
              : teams.data
                ? `${teams.data.activeTeams} active`
                : "Loading…"
          }
          detail={
            teams.isError
              ? "Could not load Teams status."
              : teams.data
                ? `${teams.data.acceptedMemberships} accepted memberships · ${teams.data.archivedTeams} archived`
                : "Checking Teams"
          }
          status={teamsStatus.status}
          tone={teamsStatus.tone}
        />
        {/* Named for the direction it measures. As "Provisioning" it carried the
            name of the tab beside it, which reports INBOUND SCIM clients, while
            these counters are the OUTBOUND projection of access changes to
            Keycloak — opposite directions under one word. */}
        <SummaryCard
          icon={RefreshCw}
          label="Access sync"
          value={
            configuration.isError
              ? "Unavailable"
              : config
                ? `${config.sync.pendingCount} queued`
                : "Loading…"
          }
          detail={
            configuration.isError
              ? "Could not load the access sync queue."
              : config
                ? [
                    `${config.sync.deadLetterCount} failed`,
                    ...(stalledCount > 0 ? [`${stalledCount} stalled`] : []),
                    ...(waitingCount > 0
                      ? [`${waitingCount} waiting for first sign-in`]
                      : []),
                    "outbound to Keycloak",
                  ].join(" · ")
                : "Checking the access sync queue"
          }
          status={accessSyncStatus.status}
          tone={accessSyncStatus.tone}
        />
      </div>

      {config && config.sync.deadLetterCount > 0 ? (
        <div className="flex gap-3 rounded-lg border border-pb-red/30 bg-pb-red-soft p-4">
          <TriangleAlert
            className="mt-0.5 h-5 w-5 shrink-0 text-pb-red-text"
            aria-hidden
          />
          <div>
            <h3 className="font-medium text-pb-text">
              {config.sync.deadLetterCount} access{" "}
              {config.sync.deadLetterCount === 1
                ? "change needs"
                : "changes need"}{" "}
              attention
            </h3>
            <p className="mt-1 text-sm text-pb-text-secondary">
              Retry the affected projections from Activity. Retrying resends the
              committed change and does not change anyone’s access.
            </p>
            {failures.data?.failures.length ? (
              <p className="mt-2 text-sm text-pb-red-text">
                Affected:{" "}
                {failures.data.failures
                  .map(
                    (failure) =>
                      failure.displayName ?? failure.email ?? "Unnamed member",
                  )
                  .join(", ")}
              </p>
            ) : null}
            {failures.isError ? (
              <p role="alert" className="mt-2 text-sm text-pb-red-text">
                Could not load the affected members.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      <Card>
        <CardBody className="grid gap-5 md:grid-cols-3">
          <OverviewMetric
            label="People"
            value={
              directoryData ? String(directoryData.people.activeCount) : "—"
            }
            note={
              directoryData?.people.suspendedCount ? (
                /* A departing person keeping access is what directory sync exists to
                   prevent, so the count has to lead somewhere. Deep-links into the
                   roster the Members page already filters server-side rather than
                   duplicating that list here (PB-5332 D18). */
                <Link asChild>
                  <RouterLink to={`${ADMIN_MEMBERS_ROUTE}?status=suspended`}>
                    {`${directoryData.people.suspendedCount} suspended`}
                  </RouterLink>
                </Link>
              ) : (
                "Active workspace members"
              )
            }
          />
          <OverviewMetric
            label="Directory groups"
            value={
              directoryData ? String(directoryData.groups.activeCount) : "—"
            }
            note="Synced from Entra or SCIM"
          />
          <OverviewMetric
            label="Reporting lines"
            /* THREE states, not two (PB-5332 review N1, same class as B3).
               `directoryData?.hierarchy.available ? count : "Unavailable"`
               collapsed "still loading" into "Unavailable", so for the first
               moment of every visit this metric asserted that reporting lines
               could not be maintained — while the two metrics beside it
               correctly showed the unknown marker. "Unavailable" is a claim
               about a withdrawn permission or a failed run; it must wait until
               something actually said so. */
            value={
              !directoryData
                ? "—"
                : directoryData.hierarchy.available
                  ? String(directoryData.hierarchy.membersWithManagerCount)
                  : "Unavailable"
            }
            note="Manager relationships available to the platform"
          />
        </CardBody>
      </Card>
    </div>
  );
}

/** Colour and wording are separate: one four-word vocabulary could not answer
    the four different questions these cards ask (PB-5404). */
type CardStatus = {
  readonly status: string;
  readonly tone: "healthy" | "watch" | "critical" | "neutral";
};

function SummaryCard({
  icon: Icon,
  label,
  value,
  detail,
  status,
  tone,
}: {
  icon: typeof KeyRound;
  label: string;
  value: string;
  /* A node, not a string: a detail that carries an instant also carries the
     zone label that says which zone it is in (PB-5409). */
  detail: ReactNode;
  status: string;
  tone: CardStatus["tone"];
}): ReactElement {
  /* StatTile (look row OK-68): the label, value and detail are KPIValue's, the
     glyph sits at the top-end corner and the status pill goes under the value. */
  return (
    <StatTile
      label={label}
      value={value}
      description={detail}
      icon={<Icon />}
      statusSlot={<StatusPill tone={tone}>{status}</StatusPill>}
    />
  );
}

function OverviewMetric({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note: ReactNode;
}): ReactElement {
  return (
    <div>
      <p className="text-sm font-medium text-pb-text-secondary">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-pb-text">{value}</p>
      <p className="mt-1 text-dense-1 text-pb-text-muted">{note}</p>
    </div>
  );
}
